import { useCallback, useEffect, useRef, useState } from 'react';
import type { UpdaterAnswer, UpdaterEvent, UpdaterJob } from '../types/modpackUpdater';
import { modpackUpdaterApi } from '../services/modpackUpdaterApi';
import { mergeUpdaterEvents, updaterActive, updaterFileDependency, UPDATER_FILE_LIMIT } from '../utils/modpackUpdater';

const message = (error: unknown) => error instanceof Error ? error.message : 'The machine service could not be reached.';

/** Shared live transport for updater and deep-clean panels. Nothing persists in browser storage. */
export function useMachineJob(selected: string | null, canWrite: boolean, ready: boolean, onGroupDiscovered?: (id: string) => void) {
  const [job, setJob] = useState<UpdaterJob | null>(null);
  const [events, setEvents] = useState<UpdaterEvent[]>([]);
  const [pollError, setPollError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const generation = useRef(0);
  const answeredRequests = useRef(new Set<string>());
  useEffect(() => { setMutationError(null); }, [job?.pending_request?.id]);
  useEffect(() => { const tick = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(tick); }, []);
  useEffect(() => {
    generation.current += 1;
    setJob(null);
    setEvents([]);
    setLastSync(null);
    setPollError(null);
    setMutationError(null);
    if (!selected) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let cursor = 0;
    const poll = async () => {
      let nextPoll = 1800;
      try {
        const next = await modpackUpdaterApi.job(selected, cursor, controller.signal);
        if (controller.signal.aborted) return;
        setJob(next.job.pending_request && answeredRequests.current.has(next.job.pending_request.id)
          ? { ...next.job, pending_request: null } : next.job);
        setEvents(previous => mergeUpdaterEvents(previous, next.events));
        for (const event of next.events) cursor = Math.max(cursor, event.sequence);
        if (next.events.length > 0 && cursor < next.job.last_sequence) nextPoll = 50;
        else if (!updaterActive(next.job.state)) nextPoll = 5000;
        setLastSync(Date.now());
        setPollError(null);
        if (next.job.group_id) onGroupDiscovered?.(next.job.group_id);
      } catch (error) {
        if (!controller.signal.aborted) setPollError(message(error));
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(poll, nextPoll);
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [selected, onGroupDiscovered]);

  const fresh = !!lastSync && now - lastSync < 20000 && !pollError;
  const answer = useCallback(async (requestId: string, value: UpdaterAnswer) => {
    if (!job || !canWrite || busy || !fresh || !ready || job.cancel_requested || job.state !== 'waiting' || job.pending_request?.id !== requestId || answeredRequests.current.has(requestId)) throw new Error('Wait for this exact request and fresh machine progress before answering.');
    const ownGeneration = generation.current;
    setBusy(true);
    setMutationError(null);
    try {
      const result = await modpackUpdaterApi.answer(job.id, requestId, value);
      answeredRequests.current.add(requestId);
      if (generation.current === ownGeneration) setJob(result.job);
    } catch (error) {
      if (generation.current === ownGeneration) setMutationError(message(error));
      throw error;
    } finally { setBusy(false); }
  }, [job, canWrite, busy, fresh, ready]);

  const uploadFile = useCallback(async (requestId: string, file: File) => {
    if (!job || !canWrite || busy || !fresh || !ready || job.cancel_requested
      || job.state !== 'waiting' || job.pending_request?.id !== requestId
      || job.pending_request.kind !== 'file' || answeredRequests.current.has(requestId)) {
      throw new Error('Wait for this exact file request and a fresh machine connection before uploading.');
    }
    const dependency = updaterFileDependency(job.pending_request);
    if (!dependency || dependency.size > UPDATER_FILE_LIMIT || file.size !== dependency.size) {
      throw new Error('Choose the exact file requested by the provider before uploading.');
    }
    const ownGeneration = generation.current;
    setBusy(true);
    setMutationError(null);
    try {
      const result = await modpackUpdaterApi.uploadFile(job.id, requestId, file);
      if (result.accepted !== true) throw new Error('The upload was not acknowledged. Refresh this run before trying again.');
      answeredRequests.current.add(requestId);
      if (generation.current === ownGeneration) setJob(result.job);
    } catch (error) {
      if (generation.current === ownGeneration) setMutationError(message(error));
      throw error;
    } finally { setBusy(false); }
  }, [job, canWrite, busy, fresh, ready]);

  const control = async (operation: 'cancel' | 'recover' | 'resume') => {
    if (!job || !canWrite || busy || !fresh || (operation !== 'cancel' && !ready)) throw new Error('Wait for fresh machine progress before continuing.');
    const ownGeneration = generation.current;
    setBusy(true);
    setMutationError(null);
    try {
      const result = operation === 'cancel' ? await modpackUpdaterApi.cancel(job.id) : await modpackUpdaterApi.recover(job.id, operation === 'resume' ? 'resume' : 'rollback');
      if (generation.current === ownGeneration) setJob(result.job);
      return result.job;
    } catch (error) {
      if (generation.current === ownGeneration) setMutationError(message(error));
      throw error;
    } finally { setBusy(false); }
  };
  return { job, setJob, events, pollError, lastSync, fresh, mutationError, setMutationError, busy, setBusy, answer, uploadFile, control };
}
